import { useMemo, type ReactNode } from 'react'
import { Button, SignalDot, StatusHint } from '@indipia/ui-core'
import type { OrderVendorRow } from '../../api/orderApi'
import type { ScheduleDetailTask } from '../../types/gantt.types'
import { STAGE_KIND_LABEL, type ScheduleListRow } from '../../types/scheduleList.types'
import { pickRepresentativeVendorRow } from '../../constants/scheduleList.logic'
import {
  getForemanLaneSteps, getOrderLaneSteps, getVendorOrderLane, getWorkLaneSteps, LANE_NO_SIGNAL_TEXT, type ListLane,
} from '../../constants/scheduleListStage.logic'
import { useTaskProgressSummary } from '../../hooks/useTaskProgress'
import { ScheduleListLane } from './ScheduleListLane'
import { cellDotColor, cellStatusText, LIST_ORDER_ERROR_TEXT, SIGNAL_TEXT_COLOR } from './listStyle'

// 펼침 영역(D-07) — 반장 배정 · 발주 · 입고 · 작업요청 · 검수 3줄 타임라인 + (고객 요청 자리) + 선행공종 줄.
// 이 컴포넌트는 행이 펼쳐질 때만 만들어지므로 작업 진행 요약(progress-summary) 조회는 펼친 행에서만, 공종당 1회 일어난다.
// 줄 문구·단계 상태는 scheduleListStage.logic 의 getForemanLaneSteps·getOrderLaneSteps·getWorkLaneSteps 값을 그대로 그린다.

export interface ScheduleListExpandProps {
  row: ScheduleListRow
  /** 이 공종의 서버 상세(반장 상태·발송·회신·배정 출처) */
  task: ScheduleDetailTask
  /** 이 일정의 발주 원장 행 전체. 발주 조회 실패(order-error)면 빈 배열이어도 된다 */
  vendorRows: readonly OrderVendorRow[]
  /** 선행공종 줄이 같은 현장 공종의 작업 상태를 찾는 데 쓰는 전체 행(taskId 기준) */
  rowsById: ReadonlyMap<number, ScheduleListRow>
  /** 'YYYY-MM-DD' 로컬 달력일 */
  today: string
  /** [공종 상세창에서 수정 ›] 실행(useScheduleListActions.openGanttDetail) */
  onOpenDetail: (taskId: number) => void
  /** 간트 상세창 열기가 연결돼 있는지. false 면 버튼을 숨긴다 */
  canOpenDetail: boolean
  /** 고객 요청 줄 자리(계획서 6-15). 주지 않으면 줄을 그리지 않는다 */
  customerRequest?: ReactNode
}

function LineHead({ children }: { children: ReactNode }) {
  return <span style={{ minWidth: 'var(--num-64)', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)' }}>{children}</span>
}

export function ScheduleListExpand({
  row, task, vendorRows, rowsById, today, onOpenDetail, canOpenDetail, customerRequest,
}: ScheduleListExpandProps) {
  const workActive = row.cells.work.status === 'stage'
  const progress = useTaskProgressSummary(workActive ? row.taskId : null)

  const lanes = useMemo<ListLane[]>(() => {
    const base = { today, startDate: row.startDate, endDate: row.endDate }
    const foreman = getForemanLaneSteps({
      ...base, cell: row.cells.foreman, item: row.signals.FOREMAN, task, partnerName: row.foremanName,
    })
    const taskVendorRows = vendorRows.filter(v => v.taskId === row.taskId && v.orderStatusCode !== 'REPLACED')
    const repRow = pickRepresentativeVendorRow(taskVendorRows, row.signals.ORDER?.reasonCode, today)
    const order: ListLane = row.cells.order.status === 'order-error'
      ? {
          kind: 'ORDER', title: STAGE_KIND_LABEL.ORDER, counterpart: row.vendorName ?? '', statusLabel: null,
          color: null, reason: null, steps: [], notice: LIST_ORDER_ERROR_TEXT,
        }
      : getOrderLaneSteps({
          ...base, cell: row.cells.order, item: row.signals.ORDER, row: repRow, vendorName: row.vendorName, orderDate: repRow?.orderDate ?? null,
        })
    const work = getWorkLaneSteps({
      ...base, cell: row.cells.work, item: row.signals.WORK, progress, workdays: row.duration, partnerName: row.foremanName,
      foremanNa: row.cells.foreman.status === 'none',
    })
    // 자재파트너가 여러 곳이면 대표 거래처(서버 신호 줄) 다음에 나머지 거래처마다 발주·입고 줄을 하나씩 더 그린다(SIF-2)
    const others = row.cells.order.status === 'stage' && repRow
      ? taskVendorRows.filter(v => v.orderVendorId !== repRow.orderVendorId)
          .sort((a, b) => a.orderVendorId - b.orderVendorId).map(getVendorOrderLane)
      : []
    return [foreman, order, ...others, work]
  }, [row, task, vendorRows, today, progress])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-05)', padding: 'var(--spacing-04) var(--spacing-05)' }}>
      {lanes.map((lane, i) => <ScheduleListLane key={`${lane.kind}-${i}`} lane={lane} />)}

      {customerRequest != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-03)' }}>
          <LineHead>고객 요청</LineHead>
          {customerRequest}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--spacing-03)' }}>
        <LineHead>선행공종</LineHead>
        {row.predecessors.length === 0 ? (
          <StatusHint tone="muted">없음</StatusHint>
        ) : (
          row.predecessors.map(p => {
            const pr = rowsById.get(p.taskId)
            const cell = pr?.cells.work
            return (
              <span key={p.taskId} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-02)', fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface)' }}>
                <SignalDot color={cell ? cellDotColor(cell) : 'NONE'} size={8} />
                {p.workTypeName}
                <span style={{ color: cell && cell.status === 'stage' ? SIGNAL_TEXT_COLOR[cell.stage.color] : 'var(--shell-hint)', fontSize: 'var(--type-caption-size)' }}>
                  · {cell ? cellStatusText(cell) : LANE_NO_SIGNAL_TEXT}
                </span>
              </span>
            )
          })
        )}
        {canOpenDetail && (
          <Button variant="ghost" size="sm" className="whitespace-nowrap" onClick={() => onOpenDetail(row.taskId)}>
            공종 상세창에서 수정 ›
          </Button>
        )}
      </div>
    </div>
  )
}
